import { apiRequest } from "@/api/softphone_api.js";
import {
  checkCallLog,
  finishCallLog,
  updateCallLogTypification,
} from "@/api/callTypificationApi.js";
import { toastInfo } from "@/modules/shared/helpers/sweetalert2";

const CHECK_RETRY_MS = 1500;
const DUPLICATE_WINDOW_MS = 10000;
// Un segundo aviso pegado al anterior es el eco del mismo colgado, no otra
// llamada: nadie cuelga dos llamadas en tres segundos. Ver el guard.
const HANGUP_ECHO_MS = 3000;
let lastHandled = { callId: null, phone: null, at: 0 };

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const toastCallLog = (title, icon = "success") =>
  toastInfo.fire({ title, icon });

const CLASSIFICATION_LABEL = {
  effective: "efectiva",
  failed: "fallida",
};

export const notifyCrmCallLog = async ({ commit, dispatch, state }) => {
  // Foto de la llamada ANTES de que HANGUP_CALL limpie el estado.
  const ctx = state.crmContext;
  const callId = ctx?.callId || state.ctxSip?.currentSession?.call_id;
  const phone =
    ctx?.phone_dialed ||
    state.callNumber ||
    state.seconduserInfo?.extension ||
    "";
  const direction = state.callDirection === "INCOMING" ? "inbound" : "outbound";
  const timerDuration = state.callDuration;
  const origin = ctx ? "crm" : "fexcom";
  const itxManagement = ctx?.itx_management;

  // hangupCall y los eventos SIP (bye/cancel) pueden llegar los dos: una sola vez
  // por llamada.
  //
  // Se recuerdan los DOS identificadores a propósito, y basta con que coincida
  // uno. Antes se comparaba una sola clave (`callId` y, si no había, el
  // teléfono), y eso dejaba pasar duplicados porque la clave NO es estable
  // entre una invocación y la siguiente: `hangupCall` limpia el estado 100 ms
  // después de disparar esta acción, así que la primera pasada suele llevar
  // `call_id` y la segunda ya no, y cae al teléfono. Al no coincidir, la
  // llamada se procesaba dos veces y el modal se reabría con datos distintos
  // —ya sin `itx_management`, porque el `finally` había limpiado el contexto—,
  // con lo que las interacciones candidatas cambiaban delante del usuario.
  // Dependía de cuánto tardara el `bye` del PBX, así que salía intermitente.
  //
  // ⚠️ Los identificadores NO sirven para emparejar las dos pasadas, y por eso
  // el guard original dejaba pasar duplicados. Verificado en red el 2026-10-22,
  // al colgar una sola llamada salían dos `check/`, dos `finish/` y dos
  // `CrmTypification/`:
  //
  //     check/?phone=573173667827     ← primera pasada
  //     check/?phone=3173667827       ← segunda, MISMA llamada
  //
  // El teléfono cambia de formato porque cada pasada lo saca de una fuente
  // distinta (`crmContext.phone_dialed`, `callNumber`, la extensión), y el
  // `call_id` no viaja en ninguna de las dos. `hangupCall` limpia el estado
  // 100 ms después de disparar, así que la segunda ve otra cosa.
  //
  // De ahí el guard por TIEMPO: lo que identifica el duplicado no es la
  // llamada, es la cercanía. Se conserva además el cotejo por identificador
  // para la ventana larga, por si llegaran dos avisos más separados.
  const now = Date.now();
  const sinceLast = now - lastHandled.at;
  const sameCall =
    sinceLast < DUPLICATE_WINDOW_MS &&
    ((callId && callId === lastHandled.callId) || phone === lastHandled.phone);
  const isEcho = sinceLast < HANGUP_ECHO_MS;
  if (!phone || sameCall || isEcho) {
    commit("CLEAR_CRM_CONTEXT");
    return;
  }
  lastHandled = { callId: callId || null, phone, at: now };

  try {
    let { data: check } = await checkCallLog({ callId, phone, itxManagement });
    // El ARI escribe la llamada en Redis al terminar. Se reintenta una vez solo para
    // conseguir el call_id real (duración y enlace con el CDR); sin él se sigue igual.
    if (!check.call_id) {
      await wait(CHECK_RETRY_MS);
      ({ data: check } = await checkCallLog({ callId, phone, itxManagement }));
    }
    if (!check.in_crm) return;

    const call = {
      callId: check.call_id || callId || null,
      phone: check.phone || phone,
      direction: check.direction || direction,
      duration: check.duration ?? timerDuration,
      origin,
      // Si se marcó desde una interacción manda esa; check/ solo decide en llamadas a mano.
      itxManagement: itxManagement ?? check.itx_management,
      interaction: check.interaction || null,
      candidates: itxManagement ? [] : check.candidates || [],
    };
    // La llamada se registra ya, sin tipificar: así no se pierde si el usuario no
    // abre el modal o cierra el navegador. El modal solo la tipifica después.
    const registered = await dispatch("registerCall", {
      call,
      silent: check.show_modal,
    });
    if (registered && check.show_modal) {
      commit("OPEN_TYPIFICATION_MODAL", {
        ...call,
        callLogId: registered.id,
        linkedItx: registered.itxManagement ?? call.itxManagement,
      });
    }
  } catch (e) {
    console.error("[CRMCALL] check/finish falló", e);
    toastCallLog("No se pudo registrar la llamada", "error");
    // Falló: se olvida para que un reintento del usuario sí pase el guard.
    if (lastHandled.phone === phone) {
      lastHandled = { callId: null, phone: null, at: 0 };
    }
  } finally {
    commit("CLEAR_CRM_CONTEXT");
  }
};

// Registra la llamada sin tipificar y devuelve { id, itxManagement }.
export const registerCall = async ({ dispatch }, { call, silent = false }) => {
  const payload = {
    ...(call.callId ? { call_id: call.callId } : {}),
    typification: null,
    origin: call.origin,
    phone: call.phone,
    direction: call.direction,
    duration: call.duration,
    ...(call.itxManagement ? { itx_management: call.itxManagement } : {}),
  };
  const { data } = await finishCallLog(payload);
  if (!data.registered) {
    if (data.reason !== "not_in_crm") {
      throw new Error(`finish/ no registró la llamada: ${data.reason}`);
    }
    return null;
  }
  if (!silent) toastCallLog("Llamada guardada sin tipificar");
  dispatch("callLogPosted", { itx_management: data.call?.itx_management });
  return { id: data.call?.id ?? null, itxManagement: data.call?.itx_management ?? null };
};

// Tipifica una llamada ya registrada; con `relink` repite finish/ para moverla de
// gestión. Devuelve { ok, error? }: el modal queda abierto si el back la rechaza.
export const saveCallTypification = async (
  { commit, dispatch },
  { call, typification, relink = false }
) => {
  if (typification === null && !relink) {
    commit("CLOSE_TYPIFICATION_MODAL");
    return { ok: true };
  }
  try {
    let data;
    if (relink) {
      ({ data } = await finishCallLog({
        ...(call.callId ? { call_id: call.callId } : {}),
        typification,
        origin: call.origin,
        phone: call.phone,
        direction: call.direction,
        duration: call.duration,
        itx_management: call.itxManagement,
      }));
    } else {
      ({ data } = await updateCallLogTypification(call.callLogId, typification));
    }
    commit("CLOSE_TYPIFICATION_MODAL");
    const classification =
      data.classification ??
      (data.typification_data ? (data.typification_data.is_effective ? "effective" : "failed") : null);
    const label = CLASSIFICATION_LABEL[classification];
    toastCallLog(
      label
        ? `Llamada tipificada como ${label}`
        : `Llamada registrada en la interacción #${call.itxManagement}`
    );
    dispatch("callLogPosted", { itx_management: call.itxManagement });
    return { ok: true };
  } catch (e) {
    const detail = e.response?.data?.typification;
    if (e.response?.status === 400 && detail) {
      return { ok: false, error: [].concat(detail).join(" ") };
    }
    console.error("[CRMCALL] no se pudo tipificar la llamada", e);
    commit("CLOSE_TYPIFICATION_MODAL");
    toastCallLog("No se pudo guardar la tipificación", "error");
    return { ok: false, error: null };
  }
};

// Evento sintético: dispatcheado tras crear exitosamente un CrmCallLog.
// Los componentes interesados (ej. Detail.vue) se suscriben via store.subscribeAction
// para refrescar el detalle de la interacción sin race conditions.
export const callLogPosted = (_, payload) => payload;

export const setCallEvents = ({ commit, state }, callEvents) => {
  if (state.phoneStatus === 'IN_CALL' || state.phoneStatus === 'BUSY'){
    return
  }
  let filteredEvents = callEvents.filter(
    (x) =>
      x.eventType == "RINGING_GROUP" ||
      x.eventType == "RINGING_IVR" ||
      x.eventType == "IVR_DMTF" ||
      x.eventType == "ORIGIN_DID"
  );
  commit("SET_PHONE_STATE", {
    phoneVar: "callEvents",
    phoneState: filteredEvents,
  });
};
export const incommingCall = ({ commit, state, rootState }, callNumber) => {
  console.log("Action INCOMING: ", state);
  console.log("Spy Target Info: ", state.spyTargetInfo);

  const user = rootState.auth_store.userInfo;
  let callDirection = "INCOMING";

  commit("SET_CALL_DIRECTION", callDirection);

  apiRequest
    .getLabel(callDirection, callNumber, user.User_id, "call", user.Token)
    .then((response) => {
      let callInfo = response.data;
      console.log("Original label:", callInfo.label);
      console.log("Original number:", callInfo.number);

      callInfo["status"] = "Llamada entrante";

      const isAnonymous =
        callInfo.label === "Desconocido" && callInfo.number === "Anonymous";

      // Si spyTargetInfo no está en este tab, buscar en localStorage (puede haber llegado de otra pestaña)
      let spyTargetInfo = state.spyTargetInfo;
      if (!spyTargetInfo && isAnonymous) {
        const stored = localStorage.getItem('spy_target_info');
        if (stored) {
          try {
            spyTargetInfo = JSON.parse(stored);
            commit("SET_SPY_TARGET_INFO", spyTargetInfo);
          } catch (e) {
            console.warn("Error al leer spy_target_info de localStorage:", e);
          }
        }
      }

      if (isAnonymous && spyTargetInfo) {
        console.log("Reemplazando info con spyTargetInfo");

        // Reemplazar campos claves
        callInfo.label = spyTargetInfo.name;
        callInfo.number = spyTargetInfo.extension;
        callInfo.avatar = spyTargetInfo.avatar;
        callInfo.status = `🕵️ Espiando a ${spyTargetInfo.extension}`;
        if (spyTargetInfo.extension.length <= 4) {
          callInfo.info = `Extensión - ${spyTargetInfo.extension}`;
        } else {
          callInfo.info = `Número - ${spyTargetInfo.extension}`;
        }
      }

      // Actualizar interfaz
      commit("SET_PHONE_STATE", { phoneVar: "showPhone", phoneState: true });
      commit("UPDATE_CALL_INFO", callInfo);
      commit("START_CALL");

      const userInfo = {
        name: callInfo.label,
        avatar: callInfo.avatar,
        extension: callInfo.number,
      };

      commit("SET_PHONE_STATE", {
        phoneVar: "seconduserInfo",
        phoneState: userInfo,
      });

      state.showCallButton = false;
      state.showAnswerButton = true;
      state.ctxSip.startRingTone();
    })
    .catch((error) => {
      console.log("Error en incoming call: ", error);
      let callInfo = {
        status: "Llamada entrante",
        label: "Desconocido",
        info: "Número - " + callNumber,
      };
      commit("SET_PHONE_STATE", { phoneVar: "showPhone", phoneState: true });
      commit("UPDATE_CALL_INFO", callInfo);
      state.showAnswerButton = true;
      state.showCallButton = false;
      commit("START_CALL");
      state.ctxSip.startRingTone();
    });
};



export const convertSDP = ({ commit, state, rootState }) => {
  const user = rootState.auth_store.userInfo;
  console.log(commit, "converSDP");
  let localDesc =
    '"' +
    state.peerConnection.localDescription.sdp
      .replaceAll(String.fromCharCode(13), "\\r")
      .replaceAll(String.fromCharCode(10), "\\n") +
    '"';
  let remoteDesc = state.peerConnection.remoteDescription.sdp
    .replaceAll(String.fromCharCode(13), "\\r")
    .replaceAll(String.fromCharCode(10), "\\n");
  apiRequest.convertSDP(localDesc, remoteDesc, user.Token).then(
    (response) => {
      console.log(response.data);
    },
    (error) => {
      console.log(error);
    }
  );
};
export const clickToCall = ({ commit, state }, number) => {
  console.log('CLICK_TOCALL', number)
  commit('SET_PHONE_STATE', { phoneVar: 'callNumber', phoneState: number })
  commit('SET_PHONE_STATE', { phoneVar: 'showPhone', phoneState: true })
  const inCall = state.userInCall
  const inConference = state.userInConference

  if (inCall == true && inConference == false) {
    this.callConferenceMember(number);
    this.initTransfer({ transferType: 'conference', transferTarget: number })
  } else if (inConference == true) {
    this.addConferenceMember(number);
  } else {
    this.outgoingCall();
  }
};
export const outgoingCall = ({ commit, state, rootState }) => {
  const user = rootState.auth_store.userInfo;
  console.log("action outgoingCall");
  let callDirection = "OUTGOING";
  let number = state.callNumber;
  if (number == "") {
    state.showCallButton = true;
    state.showAnswerButton = false;
    return;
  }
  commit("SET_CALL_DIRECTION", callDirection);
  apiRequest.getLabel(callDirection, number, user.User_id, "call", user.Token).then(
    (response) => {
      let callInfo = response.data;
      callInfo.info = `${callInfo.info.split("-")[0]}- ${state.callNumber}`;
      callInfo["status"] = "Llamando";
      commit("UPDATE_CALL_INFO", callInfo);
      state.showCallButton = false;
      state.showAnswrButton = false;
      commit("START_CALL");
      state.ctxSip.phoneCallButtonPressed();

      // update second user in outgoing call
      let userInfo = {
        name: callInfo.label,
        avatar: callInfo.avatar,
        extension: callInfo.number,
      };
      commit("SET_PHONE_STATE", {
        phoneVar: "seconduserInfo",
        phoneState: userInfo,
      });
      // Vue.set(state.inCallPeers, 0, {})
      // Vue.set(state.inCallPeers, 1, userInfo)
    },
    (error) => {
      let callInfo = {
        status: "Llamando",
        label: "Desconocido",
        info: "Número - " + number,
      };
      commit("UPDATE_CALL_INFO", callInfo);
      state.showCallButton = false;
      state.showAnswerButton = false;
      commit("START_CALL");
      state.ctxSip.phoneCallButtonPressed();
      console.log(error);
    }
  );
  console.log("after AXIOS");
};
export const updateSecondUser = (
  { commit, rootState },
  numbersInCall
) => {
  const user = rootState.auth_store.userInfo;
  let secondExtension = numbersInCall.filter((x) => x != user.Display)[0];
  apiRequest.getExtensionInfo(secondExtension, user.Token).then(
    (response) => {
      let userInfo = response.data;
      commit("SET_PHONE_STATE", {
        phoneVar: "seconduserInfo",
        phoneState: userInfo,
      });
      //   Vue.set(state.inCallPeers, 1, userInfo);
    },
    (error) => {
      console.log(error);
    }
  );
};
export const getLastNumber = ({ state, rootState }) => {
  const token = rootState.auth_store.userInfo.Token;
  console.log("Action GET LAST NUMBER");
  apiRequest.getLastNumber(token, rootState.auth_store.userInfo.User).then(
    (response) => {
      state.callNumber = response.data.number;
    },
    (error) => {
      console.log(error);
    }
  );
};
export const preTranslateNumber = ({ state, rootState }) => {
  const token = rootState.auth_store.userInfo.Token;
  console.log("Action prestraslate number: ", state.callNumber);
  apiRequest.preTranslateNumber(token, state.callNumber).then(
    (response) => {
      console.log(response);
      state.callNumber = response.data;
    },
    (error) => {
      console.log(error);
    }
  );
};
export const acceptCall = ({ commit, state}) => {
  console.log("AcceptCallState:", state);
  state.ctxSip.clickToDial = null;
  state.ctxSip.stopRingTone();
  state.showCallButton = false;
  state.showAnswerButton = false;
  commit("UPDATE_CALL_INFO", { status: "Conectando..." });
  state.ctxSip.fireAnswerEvent();
};
export const answerCall = ({ commit, state }) => {
  console.log("answerCallState:", state);
  state.ctxSip.clickToDial = null;
  commit("ANSWER_CALL");
  commit("INIT_STATISTIC");
  const isSpyCall = !!state.spyTargetInfo;
  
  if (isSpyCall ) {
    const spyStatus = `🕵️ Espiando a ${state.spyTargetInfo.extension}`;
    commit("UPDATE_CALL_INFO", { status: spyStatus });
  } else {
    // Llamada normal, se actualiza a En llamada
    commit("UPDATE_CALL_INFO", { status: "En llamada" });
  }
};
export const hangupCall = ({ commit, dispatch, state }) => {
  console.log("action hangupCall");
  if (state.userInConference === true) {
    commit("TOGGLE_CONFERENCE_RECORD");
  //   dispatch("toggleConferenceRecord");
  }
  if (state.ctxSip.clickToDial) {
    // notify zoho click to call rejected
    // vueApp.rejectClickToCall("rejected");
  }

  dispatch("notifyCrmCallLog");

  setTimeout(() => {
    commit("HANGUP_CALL")
    commit("CLEAR_SPY_TARGET_INFO")
    state.ctxSip.stopRingTone()
    state.ctxSip.sipHangUp(state.ctxSip.callActiveID)
  }, 100)
};
export const finishCall = ({ state, commit, dispatch }) => {
  state.ctxSip.stopRingTone();
  state.ctxSip.clickToDial = null;
  dispatch("notifyCrmCallLog");
  const callInfoSurvey = state.ctxSip.currentSession.call_id;
  if (callInfoSurvey) {
    const [, call_idSurvey] = callInfoSurvey.split(":");
    console.log(call_idSurvey);
    // vueApp.lastCallSurvey.call_id = call_idSurvey;
    commit("HANGUP_CALL");
    commit("CLEAR_SPY_TARGET_INFO");
    setTimeout(() => {
      //   vueApp.showModal();
    }, 2000);
  }
  if (!callInfoSurvey) {
    commit("HANGUP_CALL");
    commit("CLEAR_SPY_TARGET_INFO");
  }
};
export const pressDigit = ({ commit, state }, digit) => {
  commit("APPEND_DIGIT", digit);
  state.ctxSip.sipSendDTMF(digit);
};
export const pressMute = ({ state }) => {
  state.isMuted = !state.isMuted;
  state.ctxSip.phoneMuteButtonPressed(state.ctxSip.callActiveID);
};
export const pressHold = ({ state }) => {
  state.isHold = !state.isHold;
  state.ctxSip.phoneHoldButtonPressed(state.ctxSip.callActiveID);
};
export const initTransfer = (
  { commit, state, dispatch, rootState },
  { transferType, transferTarget }
) => {
  const user = rootState.auth_store.userInfo;
  const token = user.Token;
  const call_id = state.ctxSip.currentSession.call_id;
  console.log("commit en init tranfer:", commit);
  console.log("commit en init tranfer:", state.userInConference);
  // target = window.prompt('Ingrese número destino', '');

  if (state.userInConference == true) {
    let alreadyInConference = state.conferenceMembers.filter(
      (x) => x.extension == transferTarget
    ).length;

    if (alreadyInConference != 0) {
      //   alertError(
      //     `No se puede agregar al número ${transferTarget} porque ya esta en la conferencia`
      //   );
      return;
    }
  } else if (state.recordConference === false) {
    setTimeout(() => dispatch("toggleConferenceRecord"), 5000);
  }

  if (
    state.ctxSip.currentSession.numbers_in_call.includes(String(transferTarget))
  ) {
    // alertError(
    //   `No se puede transferir al número ${transferTarget} porque ya esta en la llamada`
    // );
    return;
  }

  apiRequest
    .pbxTransfer(transferType, transferTarget, call_id, user, token)
    .then((response) => {
      console.log(response);
    })
    .catch((error) => {
      //   alertError(error.response.data.error);
      console.log(error);
    });
};
export const warnTranferAction = ({ state, rootState }, transferPhase) => {
  const user = rootState.auth_store.userInfo;
  const token = user.Token;
  const call_id = state.ctxSip.currentSession.call_id;
  state.warnTransfer = false;
  apiRequest
    .warnTransfer(transferPhase, call_id, user, token)
    .then((response) => {
      console.log(response);
    })
    .catch((error) => {
      //   alertError("Error al transferir la llamada");
      console.log(error);
    });
};
export const restartCallDuration = ({ state }) => {
  state.callDuration = 0;
};
export const setTranferParams = ({ state }, { callId, transferType }) => {
  state.isTransferInvited = true;
  state.transferCallId = callId;
  state.transferType = transferType;
};
export const rejecttranferInvitation = ({ state, commit, rootState }) => {
  const user = rootState.auth_store.userInfo;
  console.log(commit);
  apiRequest
    .rejecttranferInvitation(state.transferCallId, state.transferType, user)
    .then((response) => {
      console.log(response);
    })
    .catch((error) => {
      //   alertError(error.response.data.error);
      console.log(error);
    });
  state.isTransferInvited = false;
  state.transferCallId = "";
  state.transferType = "";
};

// Actions for call conference
export const callConferenceMember = ({ commit, rootState }, number) => {
  const user = rootState.auth_store.userInfo;
  console.log("entro accion callConferenceMember");
  let callDirection = "OUTGOING";

  apiRequest
    .getLabel(callDirection, number, user.User_id, "call", user.Token)
    .then(
      (response) => {
        console.log(response);
        let callInfo = response.data;
        callInfo["status"] = "Llamando";
        commit("CALL_CONFERENCE_MEMBER", { callInfo, number });
      },
      (error) => {
        console.log(error);
        let callInfo = {
          status: "Llamando",
          label: "Desconocido",
          info: "Número - " + number,
        };
        commit("CALL_CONFERENCE_MEMBER", { callInfo, number });
      }
    );
};
export const addConferenceMember = ({ state, rootState }, member) => {
  const user = rootState.auth_store.userInfo;
  apiRequest
    .addConferenceMember(state.conferenceId, member, user)
    .then((response) => {
      console.log(response);
    })
    .catch((error) => {
      //   alertError("Error al agregar el miembro a la conferencia");
      console.log(error);
      console.log(error);
      console.log(error.message);
    });
};
export const removeConferenceMember = ({ state, rootState }, member) => {
  const user = rootState.auth_store.userInfo;
  apiRequest
    .removeConferenceMember(state.conferenceId, member, user)
    .then((response) => {
      console.log(response);
    })
    .catch((error) => {
      //   alertError("Error al agregar el miembro a la conferencia");
      console.log(error);
      console.log(error);
    });
};
export const toggleConferenceRecord = ({ state, commit, rootState }) => {
  const user = rootState.auth_store.userInfo;
  commit("TOGGLE_CONFERENCE_RECORD");
  apiRequest
    .toggleConferenceRecord(state.conferenceId, state.recordConference, user)
    .then((response) => {
      console.log(response);
    })
    .catch((error) => {
      //   alertError("Error la grabación de la conferencia");
      console.log(error);
    });
};
export const removeInvitedChannel = ({ state, commit, rootState }) => {
  const user = rootState.auth_store.userInfo;
  apiRequest
    .removeInvitedChannel(
      state.conferenceId,
      state.targetConferenceEndpoint,
      user
    )
    .then((response) => {
      console.log(response);
      commit("REMOVE_INVITED_CHANNEL");
    })
    .catch((error) => {
      //   alertError(error.response.data.error);
      console.log(error);
    });
};
// rejectConferenceInvitation ({ state, commit }) {
//   apiRequest.rejectConferenceInvitation(state.invitationData)
//   .then((response) => {
//     console.log(response)
//   })
//   .catch((error) => {
//     alertError(error.response.data.error)
//   })
// },

// Actions for ContactBrowser Component
export const setContacts = ({ commit, rootState }) => {
  const user = rootState.auth_store.userInfo;
  apiRequest
    .getContacts(user.Token)
    .then((response) => {
      commit("SET_CONTACTS", response.data);
    })
    .catch((error) => {
      console.log(error);
    });
};
export const setPjsipContacts = ({ state, rootState }) => {
  const user = rootState.auth_store.userInfo;
  apiRequest.getPjsipContacts(user).then(
    (response) => {
      console.log("setPjsipContacts", response.data);
      state.pjsipContacts = response.data;
      // commit('SET_PHONE_STATE', { phoneVar: 'pjsipContacts', phoneState: response.data })
    },
    (error) => {
      console.log(error);
    }
  );
};
export const showDialog = ({ commit }, type) => {
  commit("SHOW_DIALOG", type);
};
export const closeDialog = ({ commit }) => {
  commit("CLOSE_DIALOG");
};
